import { useMutation } from '@apollo/client'
import { Card } from '@faceup/ui'
import { ChannelType } from '@faceup/utils'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { EmailSubscriptionForm } from './EmailSubscriptionForm'

const fragments = {
  EmailNotificationsCheckbox_victim: graphql(`
    fragment EmailNotificationsCheckbox_viewer on CompanyVictim {
      emailMasked
      isEmailVerified
      case {
        channel {
          id
          sourceType
          config {
            __typename
            ... on FormReportSourceConfiguration {
              id
              emailNotificationEnabled
            }
          }
        }
      }
    }
  `),
}

const mutations = {
  EditEmailCompanyVictim: graphql(`
    mutation EditEmailCompanyVictimMutation($input: EditEmailCompanyVictimInput!) {
      editEmailCompanyVictim(input: $input) {
        companyVictim {
          id
          emailMasked
          isEmailVerified
        }
      }
    }
  `),
  ResendVictimEmailVerification: graphql(`
    mutation ResendVictimEmailVerificationMutation {
      resendVictimEmailVerification {
        result
        retryAfterSeconds
        companyVictim {
          id
          emailMasked
          isEmailVerified
        }
      }
    }
  `),
}

type EmailNotificationsCheckboxProps = {
  viewer: FragmentType<typeof fragments.EmailNotificationsCheckbox_victim> | null
}

const EmailNotificationsCheckbox = ({ viewer: _viewer }: EmailNotificationsCheckboxProps) => {
  const viewer = getFragmentData(fragments.EmailNotificationsCheckbox_victim, _viewer)
  const [editCompanyEmail] = useMutation(mutations.EditEmailCompanyVictim)
  const [resendVerification] = useMutation(mutations.ResendVictimEmailVerification)
  const channel = viewer?.case?.channel

  const isEmailNotificationEnabled =
    channel?.sourceType === ChannelType.Survey ||
    (channel?.config.__typename === 'FormReportSourceConfiguration'
      ? channel.config.emailNotificationEnabled
      : false)

  if (!channel || !isEmailNotificationEnabled) {
    return null
  }

  return (
    <Card className='overflow-hidden border border-colorBorder bg-colorPrimaryBg p-8 w-full !shadow-none'>
      <EmailSubscriptionForm
        maskedEmail={viewer?.emailMasked ?? null}
        isEmailVerified={viewer?.isEmailVerified ?? undefined}
        onSubmit={async email => {
          const { data } = await editCompanyEmail({
            variables: { input: { email } },
          })
          return Boolean(data)
        }}
        onUnsubscribe={async () => {
          const { data } = await editCompanyEmail({
            variables: { input: { email: null } },
          })
          return Boolean(data)
        }}
        onResend={async () => {
          const { data } = await resendVerification()
          const payload = data?.resendVictimEmailVerification
          if (!payload) {
            throw new Error('Empty resendVictimEmailVerification payload')
          }
          return {
            result: payload.result,
            retryAfterSeconds: payload.retryAfterSeconds ?? null,
          }
        }}
      />
    </Card>
  )
}

export default EmailNotificationsCheckbox
